import * as React from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { ApiErrorCode, parseApiError } from '@ooh/contracts';
import type { Offer } from '@ooh/contracts';
import { useTranslation } from 'react-i18next';
import { Button } from '@ooh/ui/components/button';
import { DropdownMenuItem, DropdownMenuLabel } from '@ooh/ui/components/dropdown-menu';
import { Label } from '@ooh/ui/components/label';
import { ModalDialog } from '@ooh/ui/components/modal-dialog';
import { Textarea } from '@ooh/ui/components/textarea';
import { toast } from 'sonner';
import { useSetOfferStatus } from '../api';
import { getAdminStatusOptions } from '@ooh/screens';
import { getOfferStatusLabelKey } from '../lib/offer-status';

interface OfferAdminStatusMenuItemsProps {
  offer: Offer;
  onStatusSelect: (status: Offer['status']) => void;
}

interface OfferAdminStatusDialogProps {
  offer: Offer;
  targetStatus: Offer['status'];
  onClose: () => void;
}

interface StatusReasonForm {
  reason: string;
}

export function OfferAdminStatusMenuItems({
  offer,
  onStatusSelect,
}: OfferAdminStatusMenuItemsProps) {
  const { t } = useTranslation('offers');
  const statusOptions = React.useMemo<Offer['status'][]>(
    () => getAdminStatusOptions(offer.status),
    [offer.status],
  );

  return (
    <>
      <DropdownMenuLabel>{t('workspace.header.actions.adminChangeStatus')}</DropdownMenuLabel>
      {statusOptions.map((statusOption) => {
        const status = statusOption as Offer['status'];

        return (
          <DropdownMenuItem key={String(status)} onSelect={() => onStatusSelect(status)}>
            {t(getOfferStatusLabelKey(status))}
          </DropdownMenuItem>
        );
      })}
    </>
  );
}

export function OfferAdminStatusDialog({
  offer,
  targetStatus,
  onClose,
}: OfferAdminStatusDialogProps) {
  const { t } = useTranslation('offers');
  const setOfferStatus = useSetOfferStatus();
  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<StatusReasonForm>({
    defaultValues: { reason: '' },
    mode: 'onChange',
  });
  const reasonValue = useWatch({ control, defaultValue: '', name: 'reason' });

  function closeStatusDialog() {
    reset({ reason: '' });
    onClose();
  }

  function validateReason(value: string) {
    const trimmedValue = value.trim();

    if (trimmedValue.length < 5) {
      return t('workspace.header.statusDialog.errors.reasonMin');
    }

    if (trimmedValue.length > 500) {
      return t('workspace.header.statusDialog.errors.reasonMax');
    }

    return true;
  }

  async function handleStatusSubmit(values: StatusReasonForm) {
    try {
      const updatedOffer = await setOfferStatus.mutateAsync({
        id: String(offer.id),
        reason: values.reason.trim(),
        targetStatus,
      });

      closeStatusDialog();
      toast.success(
        t('workspace.header.toasts.statusSaved', {
          campaignNumber: updatedOffer.campaignNumber ?? '—',
          status: t(getOfferStatusLabelKey(updatedOffer.status)),
        }),
      );
    } catch (error) {
      const parsedError = parseApiError(error);

      if (parsedError.statusCode === 409) {
        toast.error(t('workspace.header.toasts.statusConflict'));
        return;
      }

      if (
        parsedError.statusCode === 422 &&
        parsedError.errorCode === ApiErrorCode.OFFER_STATUS_UNCHANGED
      ) {
        toast.error(t('workspace.header.toasts.statusUnchanged'));
        return;
      }

      if (parsedError.statusCode === 422) {
        toast.error(t('workspace.header.toasts.statusValidation'));
        return;
      }

      if (parsedError.statusCode === 403) {
        toast.error(t('workspace.header.toasts.statusForbidden'));
        return;
      }

      toast.error(t('workspace.header.toasts.statusSaveError'));
    }
  }

  return (
    <ModalDialog
      closeLabel={t('workspace.header.statusDialog.close')}
      footer={
        <>
          <Button type="button" variant="outline" onClick={closeStatusDialog}>
            {t('workspace.header.statusDialog.cancel')}
          </Button>
          <Button
            type="submit"
            // eslint-disable-next-line i18next/no-literal-string -- form id, not user-visible
            form="offer-status-form"
            disabled={!isValid || setOfferStatus.isPending}
          >
            {t('workspace.header.statusDialog.submit')}
          </Button>
        </>
      }
      header={
        <div>
          <h2 className="text-base font-semibold">{t('workspace.header.statusDialog.title')}</h2>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {t('workspace.header.statusDialog.description', {
              status: t(getOfferStatusLabelKey(targetStatus)),
            })}
          </p>
        </div>
      }
      onClose={closeStatusDialog}
      testId="status-reason-dialog"
    >
      <form id="offer-status-form" onSubmit={handleSubmit(handleStatusSubmit)}>
        <div className="space-y-1.5">
          <Label htmlFor="offer-status-reason">
            {t('workspace.header.statusDialog.reasonLabel')}
          </Label>
          <Textarea
            id="offer-status-reason"
            aria-invalid={errors.reason ? true : undefined}
            error={Boolean(errors.reason)}
            placeholder={t('workspace.header.statusDialog.reasonPlaceholder')}
            {...register('reason', { validate: validateReason })}
          />
          {errors.reason ? (
            <p className="text-xs text-destructive">{errors.reason.message}</p>
          ) : null}
          <div className="flex justify-end text-xs text-muted-foreground">
            <span>{reasonValue.trim().length}/500</span>
          </div>
        </div>
      </form>
    </ModalDialog>
  );
}
